import React, { useEffect, useState } from "react";
import { Helmet } from "react-helmet";
import "./MemoryTypes.css";

// Each cell is [colorClass, text, optionalNote].
// colorClass: "yes" | "might" | "no" | "" (descriptive row, no color).
// A note adds a ✦ marker, a tooltip, and an entry in the notes section below the table.

const columns = [
  ["NumPy Array (RAM)", "One contiguous block of memory holding numbers of a single type. The CPU computes on it in place, which makes vectorised math fast. The reference point every other column is compared against."],
  ["Disk Arrays (memmap, HDF5)", "The same array layout, kept in a file. A memory-mapped array lets you read small parts of a large file without loading the whole file into memory. HDF5 adds chunking, metadata and many arrays in one file."],
  ["Zarr", "N-dimensional arrays split into chunks that are compressed and stored as separate objects, which suits cloud object storage and parallel readers."],
  ["GPU Tensor (CuPy, PyTorch)", "Arrays living in GPU memory, where thousands of cores work on them at once. Data must be copied in over the PCIe bus first, so it pays off for heavy computation."],
  ["Apache Arrow", "A standard columnar layout for data in memory, the same in every language, so processes and tools can share data without converting or copying it."],
  ["Parquet", "A columnar file format: each column is stored, encoded and compressed separately, with statistics that let readers skip irrelevant parts."],
  ["Row Database (PostgreSQL)", "The baseline from the general-purpose world: rows stored together in pages, B-tree indexes, transactions and in-place updates."],
  ["Kafka", "A distributed append-only log. It does not compute or answer questions, it moves an endless stream of records reliably between producers and consumers."],
  ["Prometheus", "A metrics database built around series: a metric name plus labels, each holding a strip of timestamp and value pairs, scraped every few seconds."],
  ["InfluxDB", "A purpose-built time series database with its own write protocol, tags and fields, and time-based retention."],
  ["TimescaleDB", "Time series as a PostgreSQL extension: ordinary SQL tables that are automatically split into time chunks."],
  ["kdb+", "A column-oriented database and the q language, long dominant for financial tick data where billions of trades and quotes are analysed."],
  ["ClickHouse", "A general columnar analytics database that is very fast at scanning and aggregating time-ordered event data at scale."],
  ["Apache Druid", "A real-time analytics database where every row has a timestamp and data is stored in time-partitioned segments, built for dashboards over event streams."],
  ["xarray / NetCDF", "Labelled N-dimensional arrays: axes have names (time, latitude, depth) and coordinates, widely used in climate and earth science."],
  ["TileDB", "A storage engine where everything is a dense or sparse multi-dimensional array, cut into tiles, on disk or cloud storage."],
];

const rows = [
  {
    name: "Time Series Native",
    desc: "Is the system designed around time as the main axis? Yes means time is built into storage, indexing and queries. No means time is just another column or axis you choose to use.",
    cells: [
      ["no", "Any Axis"],
      ["no", "Any Axis"],
      ["no", "Any Axis"],
      ["no", "Any Axis"],
      ["no", "Has Timestamp Types"],
      ["no", "Has Timestamp Types"],
      ["no", "Possible With Indexes"],
      ["might", "Arrival Order, Not Event Time", "Records are ordered by when they reached the broker, and each carries a timestamp. Kafka can find an offset by time, but it never sorts or aggregates by time."],
      ["yes", ""],
      ["yes", ""],
      ["yes", ""],
      ["yes", "Tick Data"],
      ["might", "General Columnar, Strong On Time-Ordered Data"],
      ["yes", "Timestamp Column Required"],
      ["might", "Time Coordinate Is Common"],
      ["no", "Any Dimension"],
    ],
  },
  {
    name: "Moving Dimension",
    desc: "The axis along which data grows or is traversed. For time series it is time, but it can be an arrival offset, a sort key, depth, wavelength, or any index.",
    cells: [
      ["", "Any Axis, Fixed Size"],
      ["", "Any Axis, Resizable If Chunked"],
      ["", "Any Axis, Resizable"],
      ["", "Any Axis, Fixed Size"],
      ["", "Rows, In Record Batches"],
      ["", "Rows, In Row Groups"],
      ["", "Rows, Any Order"],
      ["", "Offset (Arrival Order)"],
      ["", "Time"],
      ["", "Time"],
      ["", "Time Or Integer Column"],
      ["", "Time"],
      ["", "Sort Key, Often Time"],
      ["", "Time"],
      ["", "Any Named Dimension"],
      ["", "Any Dimension"],
    ],
  },
  {
    name: "Shape",
    desc: "How many dimensions the data naturally has: a 1-D stream, a 2-D table, or an N-dimensional grid.",
    cells: [
      ["", "N-D"],
      ["", "N-D"],
      ["", "N-D"],
      ["", "N-D Tensor"],
      ["", "2-D Table, By Column"],
      ["", "2-D Table, By Column"],
      ["", "2-D Table, By Row"],
      ["", "1-D Stream Of Records"],
      ["", "1-D Per Series, Labels As Extra Axes", "Each label combination is its own 1-D strip of values. Labels such as host or region act like extra dimensions you can slice and aggregate over."],
      ["", "Table Per Measurement"],
      ["", "2-D Table"],
      ["", "2-D Table, By Column"],
      ["", "2-D Table, By Column"],
      ["", "2-D Table, By Column"],
      ["", "N-D, Named Axes"],
      ["", "N-D, Dense Or Sparse"],
    ],
  },
  {
    name: "Where It Lives",
    desc: "The physical home of the data while it is being used: RAM, disk, GPU memory, cloud object storage, or spread across a cluster.",
    cells: [
      ["", "RAM"],
      ["", "Disk, Read In Parts"],
      ["", "Disk Or Cloud Object Storage"],
      ["", "GPU Memory"],
      ["", "RAM, Shareable Across Processes"],
      ["", "Disk Or Cloud Object Storage"],
      ["", "Disk + Buffer Cache"],
      ["", "Disk Across Brokers + OS Page Cache"],
      ["", "RAM For Recent, Disk Blocks For Older"],
      ["", "RAM + Disk"],
      ["", "Disk (PostgreSQL)"],
      ["", "RAM For Today, Disk For History"],
      ["", "Disk"],
      ["", "Deep Storage + Local Cache"],
      ["", "RAM, Backed By Files"],
      ["", "Disk Or Cloud Object Storage"],
    ],
  },
  {
    name: "Unit Of Storage",
    desc: "The smallest piece the system reads or writes as a whole. An array works element by element. Analytical systems work in chunks, columns or segments, which is what makes them fast at scale.",
    cells: [
      ["", "Element"],
      ["", "Element Or Chunk"],
      ["", "Compressed Chunk"],
      ["", "Element"],
      ["", "Column Buffer"],
      ["", "Column Chunk In Row Group"],
      ["", "Row In A Page"],
      ["", "Record In Log Segment"],
      ["", "Compressed Chunk Per Series"],
      ["", "Columnar File"],
      ["", "Time Chunk (Child Table)"],
      ["", "Column File"],
      ["", "Column Part, Read In Granules"],
      ["", "Time Segment"],
      ["", "Backend Array"],
      ["", "Tile"],
    ],
  },
  {
    name: "Indexing",
    desc: "How the system finds data without scanning everything. Arrays need no index because position is arithmetic. Databases build structures over labels, keys or time ranges.",
    cells: [
      ["yes", "Address Arithmetic", "Every element has the same width, so element i lives at start plus i times the stride. The position is the index, and it costs nothing to compute."],
      ["yes", "Offset Arithmetic, Chunk Index"],
      ["yes", "Chunk Key From Coordinates"],
      ["yes", "Address Arithmetic"],
      ["might", "Position Only, No Value Index"],
      ["might", "Min/Max Statistics For Skipping", "Parquet has no index in the database sense. Each column chunk records its minimum and maximum, so a reader looking for last week can skip chunks whose range does not overlap."],
      ["yes", "B-Tree Per Indexed Column"],
      ["might", "Sparse Offset And Time Index", "A small file per segment maps some offsets, and some timestamps, to byte positions. Nothing indexes the content of the records."],
      ["yes", "Inverted Index On Labels", "Like a search engine: each label value points to the list of series that carry it. Individual data points are never indexed."],
      ["yes", "Time + Tags"],
      ["yes", "Time Chunks + B-Trees"],
      ["yes", "Sorted And Grouped Column Attributes"],
      ["yes", "Sparse Primary Index", "The table is sorted by a key, and the index stores one entry per block of rows rather than per row. It stays small enough to live in RAM."],
      ["yes", "Time Partitions + Bitmap Indexes"],
      ["yes", "Coordinate Labels"],
      ["yes", "Tile Coordinates"],
    ],
  },
  {
    name: "Random Access By Position",
    desc: "Can you jump straight to item number N, or to one exact point, without reading its neighbours?",
    cells: [
      ["yes", "Instant"],
      ["yes", "Fast, Disk Read On Miss"],
      ["might", "Fetches Whole Chunk"],
      ["yes", "Instant"],
      ["yes", "Instant"],
      ["might", "Reads Whole Page"],
      ["might", "Via Index"],
      ["might", "Seek To Offset, Then Scan"],
      ["no", "Range Queries Only"],
      ["no", "Range Queries Only"],
      ["might", "Via Index"],
      ["yes", "Vector Indexing"],
      ["might", "Finds Block, Then Scans"],
      ["no", "Range Queries Only"],
      ["yes", "By Position Or Label"],
      ["yes", "Subarray Reads"],
    ],
  },
  {
    name: "Cheap Append",
    desc: "Can new data be added at the end cheaply and continuously? This is the core requirement of any time series or streaming system.",
    cells: [
      ["no", "Append Copies Whole Array", "A NumPy array has a fixed size. Appending allocates a new, larger block and copies everything. Growing one item at a time in a loop is a classic slow pattern."],
      ["might", "Resizable Chunked Datasets"],
      ["yes", "Append Along An Axis"],
      ["no", "Fixed Size"],
      ["might", "New Batches, Old Ones Immutable"],
      ["no", "Write Once, Add New Files"],
      ["might", "Insert Anywhere, Random I/O"],
      ["yes", "Sequential Append"],
      ["yes", ""],
      ["yes", ""],
      ["yes", "Into Newest Chunk"],
      ["yes", ""],
      ["yes", "Batch Inserts Become Parts"],
      ["yes", "Streaming Or Batch"],
      ["might", "Via Zarr Backend"],
      ["yes", "New Fragments"],
    ],
  },
  {
    name: "In-Place Updates",
    desc: "Can an existing value be changed where it sits? Time series systems usually give this up, because never editing the past is what makes appends and compression cheap.",
    cells: [
      ["yes", ""],
      ["yes", ""],
      ["yes", "Rewrites Chunk"],
      ["yes", ""],
      ["no", "Immutable Buffers"],
      ["no", "Immutable Files"],
      ["yes", "Transactions"],
      ["no", "Immutable, Compaction By Key"],
      ["no", ""],
      ["might", "Overwrite Same Point"],
      ["yes", "SQL UPDATE"],
      ["yes", ""],
      ["might", "Heavy Background Mutations"],
      ["no", "Reingest Segment"],
      ["yes", "In RAM"],
      ["might", "Newer Fragment Wins"],
    ],
  },
  {
    name: "Compression",
    desc: "Does the system shrink data on storage? Numerical streams compress very well because neighbouring values are similar and timestamps are regular.",
    cells: [
      ["no", ""],
      ["might", "Chunk Filters (HDF5)"],
      ["yes", "Per Chunk"],
      ["no", ""],
      ["might", "Optional In Files And Transfer"],
      ["yes", "Encodings + Codecs Per Column"],
      ["might", "Large Values Only"],
      ["might", "Per Record Batch"],
      ["yes", "Delta-Of-Delta + XOR", "Timestamps arrive at near-regular intervals, so only the change in the gap is stored. Consecutive values differ little, so only the differing bits are stored."],
      ["yes", "Columnar"],
      ["yes", "Columnar, For Older Chunks"],
      ["might", "Optional On Disk"],
      ["yes", "Codecs Per Column"],
      ["yes", "Dictionary + Bitmaps"],
      ["might", "Via Backend"],
      ["yes", "Filters Per Tile"],
    ],
  },
  {
    name: "Query Model",
    desc: "How you ask questions of the data: math on arrays, SQL, a dedicated query language, or simply reading forward from a position.",
    cells: [
      ["", "Vectorised Math"],
      ["", "Slicing + Math"],
      ["", "Slicing"],
      ["", "Parallel Kernels, Autograd"],
      ["", "Compute Kernels, Many Engines"],
      ["", "Read By Engines (Spark, DuckDB)"],
      ["", "SQL"],
      ["", "Read Forward From Offset"],
      ["", "PromQL"],
      ["", "SQL, InfluxQL"],
      ["", "SQL + Time Functions"],
      ["", "q"],
      ["", "SQL"],
      ["", "SQL"],
      ["", "Label Slicing, Groupby, Resample"],
      ["", "Subarray Slicing"],
    ],
  },
  {
    name: "Built-In Downsampling",
    desc: "Can the system summarise data into coarser time or index buckets by itself, such as per-minute averages from per-second readings?",
    cells: [
      ["might", "Reductions, No Buckets"],
      ["no", ""],
      ["no", ""],
      ["might", "Reductions, No Buckets"],
      ["might", "Aggregation Kernels"],
      ["no", ""],
      ["might", "GROUP BY"],
      ["no", "Needs A Stream Processor"],
      ["yes", "Rates, Recording Rules"],
      ["yes", ""],
      ["yes", "Continuous Aggregates"],
      ["yes", ""],
      ["yes", "Materialized Views"],
      ["yes", "Rollup At Ingest"],
      ["yes", "resample, coarsen"],
      ["no", ""],
    ],
  },
  {
    name: "Scales Across Machines",
    desc: "Can the data and the work be spread over many machines when one is not enough?",
    cells: [
      ["no", "One Process"],
      ["no", "One Machine"],
      ["yes", "Parallel Chunk Reads"],
      ["might", "Multi-GPU Via Frameworks"],
      ["might", "Over The Network Via Flight"],
      ["yes", "Files Split Across Workers"],
      ["might", "Replicas, Sharding Add-Ons"],
      ["yes", "Partitions Across Brokers"],
      ["no", "Single Node By Design", "One Prometheus server stores its own data. Long-term storage and clustering come from projects built on top, such as Thanos, Grafana Mimir, Cortex or VictoriaMetrics."],
      ["might", "Edition Dependent"],
      ["might", "Single Primary + Replicas"],
      ["might", ""],
      ["yes", "Shards + Replicas"],
      ["yes", ""],
      ["might", "With Dask"],
      ["might", ""],
    ],
  },
  {
    name: "Durable Storage",
    desc: "Does the data survive a crash or restart without extra work?",
    cells: [
      ["no", "Gone On Exit"],
      ["yes", ""],
      ["yes", ""],
      ["no", "Gone On Exit"],
      ["no", "In-Memory Format"],
      ["yes", ""],
      ["yes", "Write-Ahead Log"],
      ["yes", "Replicated Log"],
      ["might", "Local Disk + WAL, Not Replicated"],
      ["yes", ""],
      ["yes", ""],
      ["yes", ""],
      ["yes", ""],
      ["yes", "Deep Storage"],
      ["might", "Depends On Backend"],
      ["yes", ""],
    ],
  },
  {
    name: "Strategic Forgetting",
    desc: "Does the system drop old data on a policy? In time series this is retention: whole files or blocks past a certain age are deleted at once, never point by point.",
    cells: [
      ["no", ""],
      ["no", ""],
      ["no", ""],
      ["no", ""],
      ["no", ""],
      ["might", "Drop Old Files"],
      ["might", "DELETE, Drop Partitions"],
      ["yes", "Time Or Size Retention"],
      ["yes", "Drops Whole Blocks"],
      ["yes", "Retention Periods"],
      ["yes", "Drop-Chunks Policy"],
      ["might", "Drop Date Partitions"],
      ["yes", "TTL"],
      ["yes", "Retention Rules"],
      ["no", ""],
      ["might", "Vacuum Old Fragments"],
    ],
  },
  {
    name: "Real Time Ingest",
    desc: "Can the system take in a continuous stream of new values and make them queryable within seconds?",
    cells: [
      ["no", ""],
      ["no", ""],
      ["no", ""],
      ["no", ""],
      ["might", "Streaming Record Batches"],
      ["no", "Batch Files"],
      ["might", "At Moderate Rates"],
      ["yes", ""],
      ["yes", "Scrapes Every Few Seconds"],
      ["yes", ""],
      ["yes", ""],
      ["yes", "Tickerplant"],
      ["might", "Prefers Batches"],
      ["yes", "Directly From Kafka"],
      ["no", ""],
      ["no", ""],
    ],
  },
  {
    name: "High Cardinality",
    desc: "Does it cope with millions of distinct keys, such as one series per user ID? Behavioural event data needs this; sensor metrics usually do not. Not applicable to plain arrays, which have no keys.",
    cells: [
      ["", "Not Applicable"],
      ["", "Not Applicable"],
      ["", "Not Applicable"],
      ["", "Not Applicable"],
      ["", "Not Applicable"],
      ["", "Not Applicable"],
      ["yes", ""],
      ["yes", "Keys Cost Nothing"],
      ["no", "Each Label Combination Is A Series", "A label like user ID turns one metric into millions of series. Each one costs memory and index entries, and the server slows or runs out of memory. This kind of data belongs in a columnar store instead."],
      ["might", "Historic Weak Point"],
      ["yes", ""],
      ["yes", ""],
      ["yes", ""],
      ["might", ""],
      ["", "Not Applicable"],
      ["", "Not Applicable"],
    ],
  },
  {
    name: "Many Readers At Once",
    desc: "Can many programs or users read the same data at the same time, each at its own pace?",
    cells: [
      ["no", "One Process"],
      ["might", "Shared Read-Only Mapping"],
      ["yes", ""],
      ["no", "One Process"],
      ["yes", "Zero-Copy Sharing"],
      ["yes", ""],
      ["yes", ""],
      ["yes", "Each Consumer Keeps A Bookmark", "Every consumer group stores its own offset. A dashboard, an archiver and a fraud model can read the same stream at different speeds, and a new consumer can replay from the start."],
      ["yes", ""],
      ["yes", ""],
      ["yes", ""],
      ["yes", ""],
      ["yes", ""],
      ["yes", ""],
      ["no", "One Process"],
      ["yes", ""],
    ],
  },
  {
    name: "Year (Introduced)",
    desc: "Approximate first public release.",
    cells: [
      ["", "2006 (Numeric: 1995)"],
      ["", "1998 (HDF5)"],
      ["", "2015"],
      ["", "2015 (CuPy), 2016 (PyTorch)"],
      ["", "2016"],
      ["", "2013"],
      ["", "1996 (Postgres: 1986)"],
      ["", "2011"],
      ["", "2012"],
      ["", "2013"],
      ["", "2017"],
      ["", "2003"],
      ["", "2016"],
      ["", "2012"],
      ["", "1988 (NetCDF), 2014 (xarray)"],
      ["", "2016"],
    ],
  },
];

const colNames = columns.map(([n]) => n);

export default function NumericalDataStores() {
  const [colorOnly, setColorOnly] = useState(false);
  const [tsFilter, setTsFilter] = useState("all");

  const tsRow = rows[0].cells;
  const visible = colNames
    .map((_, i) => i)
    .filter((i) => {
      if (tsFilter === "all") return true;
      if (tsFilter === "ts") return tsRow[i][0] === "yes";
      return tsRow[i][0] !== "yes";
    });

  useEffect(() => {
    const table = document.getElementById("numdata-table");
    if (!table) return;
    const tooltip = document.createElement("div");
    tooltip.className = "tooltip";
    document.body.appendChild(tooltip);
    const allCells = table.querySelectorAll("[data-tooltip]");

    const position = (e) => {
      let x = e.clientX;
      let y = e.clientY;
      if (!x && !y) {
        const r = e.currentTarget.getBoundingClientRect();
        x = r.left + r.width / 2;
        y = r.top + r.height / 2;
      }
      const rect = tooltip.getBoundingClientRect();
      let left = x + 12;
      let top = y + 12;
      if (left + rect.width > window.innerWidth) left = window.innerWidth - rect.width - 12;
      if (top + rect.height > window.innerHeight) top = window.innerHeight - rect.height - 12;
      tooltip.style.left = `${left}px`;
      tooltip.style.top = `${top}px`;
    };
    const show = (e) => {
      tooltip.textContent = e.currentTarget.dataset.tooltip;
      tooltip.style.opacity = 1;
      position(e);
    };
    const hide = () => {
      tooltip.style.opacity = 0;
    };
    allCells.forEach((cell) => {
      cell.addEventListener("pointerenter", show);
      cell.addEventListener("pointermove", position);
      cell.addEventListener("pointerleave", hide);
      cell.addEventListener("focus", show);
      cell.addEventListener("blur", hide);
    });
    return () => {
      allCells.forEach((cell) => {
        cell.removeEventListener("pointerenter", show);
        cell.removeEventListener("pointermove", position);
        cell.removeEventListener("pointerleave", hide);
        cell.removeEventListener("focus", show);
        cell.removeEventListener("blur", hide);
      });
      document.body.removeChild(tooltip);
    };
  }, [tsFilter]);

  const notes = rows
    .map((r) => ({
      row: r.name,
      items: r.cells
        .map((c, i) => (c[2] ? { col: colNames[i], text: c[2] } : null))
        .filter(Boolean),
    }))
    .filter((r) => r.items.length);

  return (
    <div className="memory-types-page">
      <Helmet>
        <title>Numerical Data Stores Comparison | Helia Jamshidi</title>
        <meta
          name="description"
          content="Interactive comparison of arrays, streams, columnar formats and time series databases for large structured numerical data."
        />
      </Helmet>
      <h1>Numerical Data Stores</h1>
      <p>
        A companion to the Memory Types table, for one kind of memory only: large
        structured numerical data. Sensor readings, metrics, market ticks, climate
        grids, model tensors. The question is how arrays in RAM, on disk and on GPU
        compare with streams like Kafka, columnar formats, and time series databases.
      </p>
      <p>
        The data always grows or is traversed along one dimension. Often that
        dimension is time, but it can be an arrival offset, a sort key, depth or any
        index. The first row says whether a system is built around time; the filter
        below shows time series systems or the rest on their own.
      </p>
      <p>
        One idea runs through the whole table. An array makes position free: every
        element has the same width, so finding item N is arithmetic. Time series
        systems give up editing the past, and in return get cheap appends, strong
        compression, and retention by dropping whole files.
      </p>
      <p>
        Hovering on criteria, column headers and cells marked with <strong>✦</strong>{" "}
        shows explanations. The same text is collected below the table. The colours
        are a judgment call per cell: dark cyan means yes, gold partly, terracotta no.
        Uncoloured rows are descriptive.
      </p>
      <div className="table-controls">
        <label style={{ marginRight: "1rem" }}>
          <input
            type="checkbox"
            checked={colorOnly}
            onChange={(e) => setColorOnly(e.target.checked)}
          />
          Colors only
        </label>
        <label>
          Show:{" "}
          <select value={tsFilter} onChange={(e) => setTsFilter(e.target.value)}>
            <option value="all">All systems</option>
            <option value="ts">Time series native only</option>
            <option value="nonts">Not time series native</option>
          </select>
        </label>
      </div>
      <div className="table-container">
        <table
          id="numdata-table"
          className={`memory-table ${colorOnly ? "hide-text" : ""}`}
        >
          <thead>
            <tr>
              <th>Criteria</th>
              {visible.map((i) => (
                <th key={colNames[i]} data-tooltip={columns[i][1]} tabIndex={0}>
                  {colNames[i]}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.name}>
                <th data-tooltip={r.desc} tabIndex={0}>
                  {r.name}
                </th>
                {visible.map((i) => {
                  const [cls, text, note] = r.cells[i];
                  const base = `${r.name} – ${colNames[i]}`;
                  return (
                    <td
                      key={colNames[i]}
                      data-tooltip={note ? `${base}: ${note}` : base}
                      tabIndex={0}
                    >
                      {cls ? (
                        <span className={cls}>
                          {text}
                          {note ? "✦" : ""}
                        </span>
                      ) : (
                        <>
                          {text}
                          {note ? "✦" : ""}
                        </>
                      )}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="more-about">
        <h2>More About The Table Cells</h2>
        <h3>Criteria Descriptions</h3>
        <ul>
          {rows.map((r) => (
            <li key={r.name}>
              <strong>{r.name}:</strong> {r.desc}
            </li>
          ))}
        </ul>
        <h3>Systems</h3>
        <ul>
          {columns.map(([n, d]) => (
            <li key={n}>
              <strong>{n}:</strong> {d}
            </li>
          ))}
        </ul>
        <h3>Extra Notes (Row × Column)</h3>
        {notes.map((r) => (
          <div key={r.row}>
            <h4>{r.row}</h4>
            <ul>
              {r.items.map((it) => (
                <li key={it.col}>
                  <strong>{it.col}:</strong> {it.text}
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>
    </div>
  );
}
